import { useCallback, useContext, useMemo, useState } from 'react';
import { ThemeContext } from '@/theme/ThemeProvider';
import helix from '../../assets/helix.png';
import pyramid from '../../assets/pyramid.png';
import icosahedron from '../../assets/icosahedron.png';
import thorus from '../../assets/thorus.png';
import * as S from './styles';
import { FaArrowRight } from 'react-icons/fa6';
import { FriendSvg } from './FriendSvg';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/utils/CalVW';
import {
  CategoryType,
  useTradeMission,
  useMissionDetail,
  ReferralTier,
  ReferralMetadata,
  ReferralFriend,
  Mission,
} from '@/app/hooks/trade/tradeMission';
import BigNumber from 'bignumber.js';
import RewardButton from '../../RewardButton';
import {
  getClaimStatus,
  getMissionTemplateCode,
  MissionCode,
  hasTiers,
} from '../../utils';
import useNavigationHandler from '../../useNavigationHandler';
import RewardLoading from '../RewardLoading';
import { ReferralTable } from './ReferralTable';
import { TierDetailModal } from './TierDetailModal';

const missionImgs = [helix, pyramid, icosahedron, thorus];

// ✅ Referral Tier 타입 가드
const isReferralTier = (tier: any): tier is ReferralTier => {
  return (
    tier &&
    typeof tier === 'object' &&
    'minDeposit' in tier &&
    'minVolume' in tier &&
    'rewardAmount' in tier
  );
};

// Referral Metadata 타입 가드 (실제 백엔드 응답 구조 기반)
const isReferralMetadata = (metadata: any): metadata is ReferralMetadata => {
  return (
    metadata &&
    typeof metadata === 'object' &&
    Array.isArray(metadata.referrals)
  );
};

// 미션 코드별 경로 매핑
const MISSION_PATH_MAP: Record<string, string> = {
  [MissionCode.TRIGGER_ORDER_FIRST]: '/trade/2',
  [MissionCode.LEVERAGE_FIRST]: '/trade/2',
  [MissionCode.INSTANT_CLOSE_FIRST]: '/trade/2',
  [MissionCode.TPSL_SETUP_FIRST]: '/trade/2',
  [MissionCode.REFERRAL_GOAL]: '/referral',
};

const Feature = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, theme } = useContext(ThemeContext);
  const { data, getMissionReward, isLoading } = useTradeMission(
    CategoryType.FEATURE_EXPERIENCE
  ); // 카테고리별 API 호출
  const handleNavigation = useNavigationHandler();

  // Tier 모달 상태 관리 (ID로 관리하여 상세 조회)
  const [selectedMissionId, setSelectedMissionId] = useState<number | null>(
    null
  );
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);

  // 선택된 미션의 상세 정보 조회 (tier 목표량, 보상금 포함)
  const {
    data: selectedMissionDetail,
    isLoading: isDetailLoading,
    mutate: mutateSelectedMission,
  } = useMissionDetail(selectedMissionId);

  const featureMissions = data?.missions ?? []; // 이미 필터링된 데이터

  const referralMission = featureMissions.find(
    (mission) =>
      getMissionTemplateCode(mission.code) === MissionCode.REFERRAL_GOAL
  );

  // useSWR로 미션 상세 조회 (3초 캐싱)
  const { data: referralDetailMission, mutate: mutateReferralDetail } =
    useMissionDetail(referralMission?.id);

  const experienceMissions = data?.missions ?? []; // 이미 필터링된 데이터

  const steps = referralMission?.conditions?.steps;

  // ⚠️ REFERRAL 미션 전용: minDeposit + minVolume 필드 사용
  const rawTiers = referralDetailMission?.conditions?.tiers ?? [];
  const tiers = rawTiers.filter(isReferralTier) as ReferralTier[];

  const isMobile = useIsMobile();

  const Svgs = FriendSvg(isDarkMode, theme);
  const isKo = i18n.language === 'ko';

  const color = `#FF5C17`;

  // 전체 tier 보상 합계 계산
  const totalTierReward = tiers.reduce(
    (sum, tier) => sum.plus(new BigNumber(tier.rewardAmount || 0)),
    new BigNumber(0)
  );

  // 레퍼럴 metadata 추출 (타입 안전)
  const referralMetadata = useMemo(() => {
    const metadata = referralDetailMission?.userProgress?.metadata;
    if (isReferralMetadata(metadata)) {
      return metadata;
    }
    return null;
  }, [referralDetailMission]);

  // 친구 목록 (실제 배열에서 추출)
  const friends: ReferralFriend[] = referralMetadata?.referrals || [];
  const friendsCount = friends.length;

  // 전체 통계 (각 친구별 티어 합산)
  const totalAchievedTiers = friends.reduce(
    (sum, friend) => sum + friend.achievedTiers.length,
    0
  );
  const totalClaimedTiers = friends.reduce(
    (sum, friend) => sum + friend.claimedTiers.length,
    0
  );
  const totalAvailableTiers = totalAchievedTiers - totalClaimedTiers;

  // 보상 수령 + 실시간 갱신 래퍼 함수
  const handleClaimReward = useCallback(
    async (
      missionId: number,
      tierIndex?: number,
      rewardAmount?: string | number,
      referralUserId?: number
    ) => {
      try {
        await getMissionReward(
          missionId,
          tierIndex,
          rewardAmount,
          referralUserId
        );
        // 성공 시 레퍼럴 미션 상세 데이터 갱신
        await mutateReferralDetail();
      } catch (error) {
        // 에러는 getMissionReward에서 이미 처리됨
        console.error('Reward claim failed:', error);
      }
    },
    [getMissionReward, mutateReferralDetail]
  );

  // 친구초대 미션 텍스트 색상작업에 사용됨
  const Texts = [
    <>
      <span style={{ color: color }}>{t('inviteFriends')} </span>
      <span>{t('to sign up')}</span>
    </>,
    <>
      <span>{t('A Friend')} </span>
      <span style={{ color: color }}>{t('audit')} </span>
      <span>{t('to complete')}</span>
    </>,
    <>
      <span>{t('A Friend')} </span>
      <span style={{ color: color }}>{t('DepositNow')}</span>
    </>,
    <>
      <span>{t('A Friend')} </span>
      <span style={{ color: color }}>{t('StartTrading2')}</span>
    </>,
  ];

  // 친구초대 미션 텍스트 줄바꿈을 위해 사용됨
  const widths = isKo
    ? isMobile
      ? ['300px', '150px', '140px', '160px']
      : ['110px', '150px', '140px', '160px']
    : isMobile
      ? ['130px', '170px', '160px', '180px']
      : ['130px', '170px', '160px', '180px'];

  // 코드 별로 버튼 텍스트 설정
  const handleQuickLinkText = useCallback(
    (code: string) => {
      if (
        code === MissionCode.TRIGGER_ORDER_FIRST ||
        code === MissionCode.LEVERAGE_FIRST ||
        code === MissionCode.INSTANT_CLOSE_FIRST ||
        code === MissionCode.TPSL_SETUP_FIRST ||
        code === MissionCode.LEVERAGE_GOAL
      ) {
        return t(`tradeNow`);
      } else if (code === MissionCode.REFERRAL_GOAL) {
        return t(`goReferral`);
      }
      return '';
    },
    [t]
  );

  // Tier 모달 열기 (ID만 저장하여 상세 조회 트리거)
  const handleOpenTierModal = useCallback((missionId: number) => {
    setSelectedMissionId(missionId);
    setIsTierModalOpen(true);
  }, []);

  // Tier 모달 닫기
  const handleCloseTierModal = useCallback(() => {
    setIsTierModalOpen(false);
    setSelectedMissionId(null);
  }, []);

  if (isLoading || !experienceMissions || !referralDetailMission) {
    return <RewardLoading messageKey="loadingFeatureMission" />;
  }

  return (
    <S.Container>
      <S.ItemWrapper>
        {experienceMissions.map((item, index) => {
          const image = missionImgs[index % missionImgs.length];
          const isRotate = image === icosahedron;
          const code = getMissionTemplateCode(item.code);
          const itemHasTiers = hasTiers(item);
          const isReferralMission = code === MissionCode.REFERRAL_GOAL;

          return (
            <S.Item isDarkmode={isDarkMode} key={item.id}>
              <S.ItemImage
                src={image}
                style={{
                  transform: isRotate ? 'rotate(-20deg) scaleX(-1)' : 'none',
                }}
              />
              <S.MissionItemColumn>
                <S.ItemTag isDarkmode={isDarkMode}>
                  {t(`mission_name.${code}`, { defaultValue: item.name })}
                </S.ItemTag>

                <S.MissionItemDescColumn>
                  <S.ItemTitle>
                    {t(`mission_desc.${code}`, {
                      defaultValue: item.description,
                    })}
                  </S.ItemTitle>
                  <S.ItemPool>
                    {new BigNumber(item.rewardAmount || '0').toFormat()} USDT{' '}
                    {t('payOff')}
                  </S.ItemPool>
                </S.MissionItemDescColumn>
              </S.MissionItemColumn>

              {/* Tier가 있는 미션: 상세보기 버튼 */}
              {itemHasTiers && !isReferralMission ? (
                <S.ItemButton onClick={() => handleOpenTierModal(item.id)}>
                  {t('viewDetails')}
                  <S.ArrowIcon>
                    <FaArrowRight />
                  </S.ArrowIcon>
                </S.ItemButton>
              ) : (
                /* Tier가 없는 미션: 기존 보상 받기 버튼 */
                <RewardButton
                  missionId={item.id}
                  canClaimable={getClaimStatus(item, 0) === 'canClaim'}
                  getMissionStatus={getClaimStatus(item, 0)}
                  handleClaim={() =>
                    getMissionReward(item.id, undefined, item.rewardAmount)
                  }
                  style={{
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '0 12px',
                  }}
                  notCompletedComponent={
                    <S.ItemButton
                      onClick={() => {
                        const path = MISSION_PATH_MAP[code];
                        if (path) {
                          handleNavigation(path);
                        }
                      }}
                    >
                      {handleQuickLinkText(code)}
                      <S.ArrowIcon>
                        <FaArrowRight />
                      </S.ArrowIcon>
                    </S.ItemButton>
                  }
                />
              )}
            </S.Item>
          );
        })}
      </S.ItemWrapper>

      <S.FriendContainer isDarkmode={isDarkMode}>
        <S.FriendTitleContainer>
          <span>
            {t('inviteFriends')} {t('Mission')}
          </span>
          <S.FriendTitle>
            {new BigNumber(referralMission?.rewardAmount ?? '0').toFormat()}{' '}
            USDT {t('payOff')}
          </S.FriendTitle>
        </S.FriendTitleContainer>

        <S.FriendContentContainer>
          {Array.from({ length: 4 }).map((_, index) => (
            <S.FriendContentItem isDarkmode={isDarkMode}>
              <S.FriendTextBox width={widths[index]}>
                {!isMobile && Svgs[index]}
                <div>{Texts[index]}</div>
              </S.FriendTextBox>

              <S.FriendItemIndex isDarkmode={isDarkMode}>
                {index + 1}
              </S.FriendItemIndex>
            </S.FriendContentItem>
          ))}
        </S.FriendContentContainer>

        {/* REFERRAL 미션 전용 Tier별 보상 목표 섹션 (action === 'referral' AND tiers 존재) */}
        {referralDetailMission?.conditions?.action === 'referral' &&
          tiers.length > 0 && (
            <S.TierListContainer>
              {tiers.map((tier, index) => (
                <S.TierItem
                  key={index}
                  isDarkmode={isDarkMode}
                  isMobile={isMobile}
                >
                  {/* 좌측 번호 */}
                  <S.TierNumber isDarkmode={isDarkMode} isMobile={isMobile}>
                    {index + 1}
                  </S.TierNumber>

                  {/* 조건 텍스트 */}
                  <S.TierConditionText isMobile={isMobile}>
                    <span>{t('A Friend')} </span>
                    <S.HighlightText>
                      {new BigNumber(tier.minDeposit || '0').toFormat()} USDT{' '}
                    </S.HighlightText>
                    <span>{t('afterDeposit')}</span>
                    <S.HighlightText>
                      {new BigNumber(tier.minVolume || '0').toFormat()} USDT{' '}
                    </S.HighlightText>
                    <span>{t('trading')}</span>
                  </S.TierConditionText>

                  {/* 우측 보상 */}
                  <S.TierRewardBadge
                    isDarkmode={isDarkMode}
                    isMobile={isMobile}
                  >
                    {new BigNumber(tier.rewardAmount || '0').toFormat()} USDT{' '}
                    {t('rewarded')}
                  </S.TierRewardBadge>
                </S.TierItem>
              ))}
            </S.TierListContainer>
          )}

        {/* 친구별 진행 상황 테이블 (레퍼럴 미션 전용) */}
        {referralDetailMission?.conditions?.action === 'referral' && (
          <S.ReferralTableContainer>
            <ReferralTable
              friends={friends}
              tiers={tiers}
              onClaimReward={handleClaimReward}
              missionId={referralDetailMission.id}
            />
          </S.ReferralTableContainer>
        )}
      </S.FriendContainer>

      {/* Tier 상세 모달 (슬라이딩 캐러셀 내장) */}
      {selectedMissionDetail && !isDetailLoading && (
        <TierDetailModal
          mission={selectedMissionDetail}
          isOpen={isTierModalOpen}
          onClose={handleCloseTierModal}
          onClaimReward={async (missionId, tierIndex, rewardAmount) => {
            await getMissionReward(missionId, tierIndex, rewardAmount);
            // 보상 수령 후 모달 내 데이터 갱신
            await mutateSelectedMission();
          }}
        />
      )}
    </S.Container>
  );
};

export default Feature;
